import React, { useState, useEffect, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import COUNTRY_CODES from '../Components/Countrycode'
import CountryCodeSelect from '../Components/CountryCodeSelect'
import {
  applyPhoneInputValue,
  buildFullPhoneNumber,
  formatPhoneDigitHint,
  normalizeLocalPhoneDigits,
  parseMobileDigitLimits
} from '../utils/phoneNumber'
import { useSelector } from 'react-redux'
import {
  putFileToPresignedUrl,
  resolveFileContentType,
} from '../utils/s3Upload'

const API_URL = process.env.REACT_APP_API_URL

function parseApiResponse(raw) {
  let data = raw
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return raw
    }
  }
  if (data?.body != null) {
    if (typeof data.body === 'string') {
      try {
        return JSON.parse(data.body)
      } catch {
        return data.body
      }
    }
    return data.body
  }
  return data
}

function normalizeGstDetails(responseData) {
  const data = responseData?.data
  if (!data || typeof data !== 'object') return null

  return {
    gstin: data.gstin || '',
    legalName: data.legalName || '',
    tradeName: data.tradeName || '',
    state: data.state || '',
    status: data.status || '',
    taxpayerType: data.taxPayerType || '',
    constitutionType: data.constitutionType || '',
    registrationDate: data.registrationDate || ''
  }
}

function buildGstDetailsFromSupplier(supplier) {
  if (!supplier?.gstNumber) return null

  return {
    gstin: supplier.gstNumber,
    legalName: supplier.gstLegalName || '',
    tradeName: supplier.gstTradeName || '',
    state: supplier.gstState || '',
    status: supplier.gstStatus || '',
    taxpayerType: supplier.gstTaxPayerType || supplier.taxPayerType || '',
    constitutionType: supplier.gstConstitutionType || '',
    registrationDate: supplier.gstRegistrationDate || ''
  }
}

function isGstActiveStatus(status) {
  return String(status || '').trim().toLowerCase() === 'active'
}

function normalizeDescriptions(description) {
  if (!description) return []
  if (Array.isArray(description)) {
    return description.map((entry) => String(entry || '').trim()).filter(Boolean)
  }
  const text = String(description).trim()
  return text ? [text] : []
}

function buildDescriptionPayload(entries, newDescription) {
  const normalized = entries
    .map((entry) => String(entry || '').trim())
    .filter(Boolean)
  const newText = String(newDescription || '').trim()
  if (newText) {
    normalized.push(newText)
  }
  return normalized
}

const emptyContact = () => ({
  id: `contact-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  contactPerson: '',
  email: '',
  phonenumber: '',
  countryIso: 'IN'
})

function buildContactsFromSupplier(supplier, countries) {
  const rawContacts =
    Array.isArray(supplier.contacts) && supplier.contacts.length > 0
      ? supplier.contacts
      : [
          {
            contactPerson: supplier.contactPerson || '',
            email: supplier.email || '',
            phonenumber: supplier.phonenumber || ''
          }
        ]

  const parsed = rawContacts.map((c) => {
    const parsedPhone = applyPhoneInputValue(c.phonenumber, countries)
    return {
      id: `contact-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      contactPerson: c.contactPerson || '',
      email: c.email || '',
      phonenumber: parsedPhone.localDigits,
      countryIso: parsedPhone.iso2
    }
  })

  return parsed.length > 0 ? parsed : [emptyContact()]
}

const SupplyMasterEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const user = useSelector((state) => state.user)
  const userRole = user?.role || localStorage.getItem('role')
  const [formData, setFormData] = useState({
    clientname: '',
    city: '',
    state: '',
    address: '',
    status: 'active',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    branchName: '',
    bankAddress: '',
    bankCity: '',
    bankDistrict: '',
    bankState: '',
    gstNumber: ''
  })
  const [contacts, setContacts] = useState([emptyContact()])
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [existingAttachments, setExistingAttachments] = useState([]) // Array of existing attachments from API
  const [selectedFiles, setSelectedFiles] = useState([]) // Array of new files to upload
  const [s3Keys, setS3Keys] = useState([]) // Array of s3Keys for new files
  const [originalFileNames, setOriginalFileNames] = useState([]) // Array of original file names for new files
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')
  const [attachmentsToRemove, setAttachmentsToRemove] = useState([]) // Array of attachment indices or URLs to remove
  const [isVerifyingIfsc, setIsVerifyingIfsc] = useState(false)
  const [ifscDetails, setIfscDetails] = useState(null)
  const [ifscError, setIfscError] = useState('')
  const [isVerifyingGst, setIsVerifyingGst] = useState(false)
  const [isFetchingGst, setIsFetchingGst] = useState(false)
  const [gstDetails, setGstDetails] = useState(null)
  const [gstError, setGstError] = useState('')
  const [gstCaptchaImage, setGstCaptchaImage] = useState('')
  const [gstSessionCookies, setGstSessionCookies] = useState('')
  const [gstCaptchaInput, setGstCaptchaInput] = useState('')
  const [showGstCaptcha, setShowGstCaptcha] = useState(false)
  const [activeTab, setActiveTab] = useState('profile')
  const [descriptionSearch, setDescriptionSearch] = useState('')
  const [descriptionEntries, setDescriptionEntries] = useState([])
  const [newDescription, setNewDescription] = useState('')
  const MAX_FILES = 5
  const MAX_CONTACTS = 10

  const sortedCountries = useMemo(() => {
    const india = COUNTRY_CODES.find((country) => country.iso2 === 'IN')
    const rest = COUNTRY_CODES.filter((country) => country.iso2 !== 'IN').sort((a, b) =>
      a.country.localeCompare(b.country)
    )
    return india ? [india, ...rest] : rest
  }, [])

  const getCountryByIso = (iso2) =>
    sortedCountries.find((country) => country.iso2 === iso2) ||
    sortedCountries[0] ||
    null

  const getContactPhoneLimits = (countryIso) =>
    parseMobileDigitLimits(getCountryByIso(countryIso)?.mobile_digits)

  const descriptionDisplayEntries = useMemo(
    () =>
      [...descriptionEntries].reverse().map((text, index) => ({
        entryNumber: descriptionEntries.length - index,
        sourceIndex: descriptionEntries.length - 1 - index,
        text,
        isLatest: index === 0
      })),
    [descriptionEntries]
  )

  const filteredDescriptionDisplayEntries = useMemo(() => {
    const query = descriptionSearch.trim().toLowerCase()
    if (!query) return descriptionDisplayEntries
    return descriptionDisplayEntries.filter((entry) =>
      entry.text.toLowerCase().includes(query)
    )
  }, [descriptionDisplayEntries, descriptionSearch])

  const tabClass = (tab) => (activeTab === tab ? 'nav-link active' : 'nav-link')

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    if (tab !== 'description') {
      setDescriptionSearch('')
    }
  }

  useEffect(() => {
    fetchSupplier()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const fetchSupplier = async () => {
    if (!id) {
      toast.error("Supplier ID is missing", { autoClose: 2000 })
      navigate('/supplyMaster')
      return
    }

    setIsFetching(true)
    try {
      const payload = {
        action: "get-supplier",
        id: id
      }

      const response = await axios.post(API_URL, payload)

      // Parse the response - handle both string and object responses
      let responseData = response.data
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (parseError) {
          responseData = response.data
        }
      }

      if (responseData.success === true && responseData.supplier) {
        const supplier = responseData.supplier
        setContacts(buildContactsFromSupplier(supplier, sortedCountries))
        setDescriptionEntries(normalizeDescriptions(supplier.description))
        setNewDescription('')
        setFormData({
          clientname: supplier.clientname || '',
          city: supplier.city || '',
          state: supplier.state || '',
          address: supplier.address || '',
          status: supplier.status || 'active',
          accountNumber: supplier.accountNumber || '',
          ifscCode: supplier.ifscCode || '',
          bankName: supplier.bankName || '',
          branchName: supplier.branchName || '',
          bankAddress: supplier.bankAddress || '',
          bankCity: supplier.bankCity || '',
          bankDistrict: supplier.bankDistrict || '',
          bankState: supplier.bankState || '',
          gstNumber: supplier.gstNumber || ''
        })

        setGstDetails(buildGstDetailsFromSupplier(supplier))
        setGstError('')
        setGstCaptchaImage('')
        setGstSessionCookies('')
        setGstCaptchaInput('')
        setShowGstCaptcha(false)

        // Handle attachments - use formatted attachments array if available, otherwise build from attachmentUrls
        if (supplier.attachments && Array.isArray(supplier.attachments)) {
          setExistingAttachments(supplier.attachments)
        } else if (supplier.attachmentUrls && Array.isArray(supplier.attachmentUrls)) {
          // Build attachments array from attachmentUrls and attachmentFileNames
          const attachments = supplier.attachmentUrls.map((url, index) => ({
            url: url,
            fileName: supplier.attachmentFileNames && supplier.attachmentFileNames[index]
              ? supplier.attachmentFileNames[index]
              : `Attachment ${index + 1}`,
            index: index
          }))
          setExistingAttachments(attachments)
        } else if (supplier.attachmentUrl) {
          // Single attachment (backward compatibility)
          setExistingAttachments([{
            url: supplier.attachmentUrl,
            fileName: supplier.attachmentFileNames && supplier.attachmentFileNames[0]
              ? supplier.attachmentFileNames[0]
              : 'Attachment 1',
            index: 0
          }])
        } else {
          setExistingAttachments([])
        }
      } else {
        toast.error(responseData.message || "Failed to fetch supplier details", { autoClose: 2000 })
        navigate('/supplyMaster')
      }
    } catch (error) {
      console.error("Fetch supplier error:", error)
      if (error.response && error.response.data) {
        let errorData = error.response.data
        if (typeof errorData === 'string') {
          try {
            errorData = JSON.parse(errorData)
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(errorData.message || "An error occurred while fetching supplier details", { autoClose: 2000 })
      } else {
        toast.error("Network error. Please check your connection and try again.", { autoClose: 2000 })
      }
      navigate('/supplyMaster')
    } finally {
      setIsFetching(false)
    }
  }

  const handleDescriptionEntryChange = (index, value) => {
    setDescriptionEntries((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }

  const handleContactChange = (contactId, field, value) => {
    setContacts((prev) =>
      prev.map((c) => (c.id === contactId ? { ...c, [field]: value } : c))
    )
  }

  const handleContactCountryChange = (contactId, iso2) => {
    const country = getCountryByIso(iso2)
    if (!country) return
    const { max } = parseMobileDigitLimits(country.mobile_digits)
    setContacts((prev) =>
      prev.map((c) => {
        if (c.id !== contactId) return c
        return {
          ...c,
          countryIso: iso2,
          phonenumber: normalizeLocalPhoneDigits(c.phonenumber, max)
        }
      })
    )
  }

  const handleContactPhoneChange = (contactId, value, countryIso) => {
    const { max } = getContactPhoneLimits(countryIso)
    const digits = normalizeLocalPhoneDigits(value, max)
    handleContactChange(contactId, 'phonenumber', digits)
  }

  const handleContactPhonePaste = (contactId, e, countryIso) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text/plain')
    const { max } = getContactPhoneLimits(countryIso)
    const digits = normalizeLocalPhoneDigits(pasted, max)
    handleContactChange(contactId, 'phonenumber', digits)
  }

  const addContact = () => {
    if (contacts.length >= MAX_CONTACTS) {
      toast.info(`You can add up to ${MAX_CONTACTS} contacts`, { autoClose: 2000 })
      return
    }
    setContacts((prev) => [...prev, emptyContact()])
  }

  const removeContact = (contactId) => {
    setContacts((prev) => {
      if (prev.length <= 1) return prev
      return prev.filter((c) => c.id !== contactId)
    })
  }

  // Handle city input - allow alphabets, spaces, comma, hyphen, and slash
  const handleCityChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z\s,/-]/g, ''); // Allow alphabets, spaces, comma, hyphen, slash
    setFormData({ ...formData, city: value })
  }

  // Handle state input - only alphabets and spaces
  const handleStateChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z\s]/g, '') // Only allow alphabets and spaces
    setFormData({ ...formData, state: value })
  }

  // Handle IFSC field - allow alphanumeric (letters and numbers)
  const handleIfscChange = (e) => {
    const { name, value } = e.target;
    // Allow only alphanumeric characters (A-Z, a-z, 0-9)
    if (value === '' || /^[A-Za-z0-9]+$/.test(value)) {
      setFormData({ ...formData, [name]: value.toUpperCase() });
      // Clear previous verification results when IFSC changes
      setIfscDetails(null);
      setIfscError('');
    }
  };

  // Verify IFSC code
  const handleVerifyIfsc = async () => {
    const ifscCode = formData.ifscCode.trim();

    if (!ifscCode) {
      toast.error('Please enter IFSC code', { autoClose: 2000 });
      return;
    }

    if (ifscCode.length !== 11) {
      toast.error('IFSC code must be 11 characters', { autoClose: 2000 });
      return;
    }

    setIsVerifyingIfsc(true);
    setIfscError('');
    setIfscDetails(null);

    try {
      // Using public IFSC API
      const response = await axios.get(`https://ifsc.razorpay.com/${ifscCode}`);

      if (response.data) {
        setIfscDetails(response.data);

        // Auto-populate bank details in form
        setFormData(prev => ({
          ...prev,
          bankName: response.data.BANK || '',
          branchName: response.data.BRANCH || '',
          bankAddress: response.data.ADDRESS || '',
          bankCity: response.data.CITY || '',
          bankDistrict: response.data.DISTRICT || '',
          bankState: response.data.STATE || ''
        }));

        toast.success('IFSC code verified successfully', { autoClose: 2000 });
      }
    } catch (error) {
      console.error('IFSC verification error:', error);

      if (error.response && error.response.status === 404) {
        setIfscError('Invalid IFSC code. Please check and try again.');
        toast.error('Invalid IFSC code', { autoClose: 2000 });
      } else {
        setIfscError('Unable to verify IFSC code. Please try again.');
        toast.error('Verification failed. Please try again.', { autoClose: 2000 });
      }
    } finally {
      setIsVerifyingIfsc(false);
    }
  };

  const handleGstChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (value.length <= 15) {
      setFormData({ ...formData, gstNumber: value })
      setGstDetails(null)
      setGstError('')
      setGstCaptchaImage('')
      setGstSessionCookies('')
      setGstCaptchaInput('')
      setShowGstCaptcha(false)
    }
  }

  const fetchGstCaptcha = async () => {
    const response = await axios.post(API_URL, { action: 'get-gst-captcha' })
    const responseData = parseApiResponse(response.data)

    if (responseData?.success !== true || !responseData?.data?.captchaImage) {
      throw new Error(responseData?.message || 'Failed to load GST captcha')
    }

    setGstCaptchaImage(responseData.data.captchaImage)
    setGstSessionCookies(responseData.data.sessionCookies)
    setGstCaptchaInput('')
    setShowGstCaptcha(true)
    return responseData.data
  }

  const submitGstVerification = async (gstNumber, captcha, sessionCookies) => {
    const response = await axios.post(API_URL, {
      action: 'verify-gst',
      gstNumber,
      captcha: captcha.trim(),
      sessionCookies
    })
    return parseApiResponse(response.data)
  }

  const handleFetchGstCaptcha = async () => {
    const gstNumber = formData.gstNumber.trim().toUpperCase()

    if (!gstNumber) {
      toast.error('Please enter GST number', { autoClose: 2000 })
      return
    }

    setIsFetchingGst(true)
    setGstError('')
    setGstDetails(null)

    try {
      await fetchGstCaptcha()
      toast.info('Enter the captcha below and click Verify', { autoClose: 3000 })
    } catch (error) {
      console.error('GST captcha fetch error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      const message =
        errData?.message || error.message || 'Unable to load GST captcha. Please try again.'
      setGstError(message)
      toast.error(message, { autoClose: 2000 })
    } finally {
      setIsFetchingGst(false)
    }
  }

  const handleVerifyGst = async () => {
    const gstNumber = formData.gstNumber.trim().toUpperCase()

    if (!gstNumber) {
      toast.error('Please enter GST number', { autoClose: 2000 })
      return
    }

    if (!showGstCaptcha || !gstSessionCookies) {
      toast.error('Please fetch captcha first', { autoClose: 2000 })
      return
    }

    if (!gstCaptchaInput.trim()) {
      toast.error('Please enter the captcha', { autoClose: 2000 })
      return
    }

    setIsVerifyingGst(true)
    setGstError('')

    try {
      const responseData = await submitGstVerification(
        gstNumber,
        gstCaptchaInput,
        gstSessionCookies
      )

      if (responseData?.success === true) {
        const details = normalizeGstDetails(responseData)
        if (!details) {
          setGstError('GST details not found in response. Please try again.')
          toast.error('GST verification failed', { autoClose: 2000 })
          return
        }

        setGstDetails(details)
        setShowGstCaptcha(false)

        if (!isGstActiveStatus(details.status)) {
          setGstError(`GST number is not active. Current status: ${details.status || 'Unknown'}`)
          toast.error('GST number is not active', { autoClose: 2000 })
          return
        }

        toast.success('GST number verified successfully', { autoClose: 2000 })
      } else {
        const message = responseData?.message || 'Invalid GST number. Please check and try again.'
        setGstError(message)
        toast.error(message, { autoClose: 2000 })
        await fetchGstCaptcha()
      }
    } catch (error) {
      console.error('GST verification error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      const message =
        errData?.message || error.message || 'Unable to verify GST number. Please try again.'
      setGstError(message)
      toast.error(message, { autoClose: 2000 })
      try {
        await fetchGstCaptcha()
      } catch {
        // ignore refresh failure
      }
    } finally {
      setIsVerifyingGst(false)
    }
  }

  // Handle other input fields
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  // Handle file selection - multiple files (max 5 total including existing)
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) {
      return
    }

    // Check total files limit (existing + new)
    const currentExistingCount = existingAttachments.length
    const currentNewCount = selectedFiles.filter(f => f.status !== 'removed').length
    const totalCount = currentExistingCount + currentNewCount

    if (totalCount + files.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files allowed. You can upload ${MAX_FILES - totalCount} more file(s).`, { autoClose: 3000 })
      e.target.value = ''
      return
    }

    // Get userid from localStorage
    const userId = localStorage.getItem('id')
    // if (!userId) {
    //   toast.error("User session expired. Please login again.", { autoClose: 2000 })
    //   e.target.value = ''
    //   return
    // }

    // Add files to state with pending status
    const newFiles = files.map(file => ({
      file: file,
      originalName: file.name,
      status: 'pending', // pending, uploading, uploaded, error, removed
      progress: 0,
      s3Key: null,
      uploadUrl: null
    }))

    setSelectedFiles(prev => [...prev, ...newFiles])
    setIsUploading(true)
    setUploadProgress('Getting upload URLs...')

    try {
      // Prepare file names and content types arrays
      const contentTypesForPresign = files.map((f) => resolveFileContentType(f))
      const fileNames = files.map(f => f.name)

      // Step 1: Get pre-signed URLs for all files
      const presignedPayload = {
        action: "get-upload-presigned-url",
        fileNames: fileNames,
        contentTypes: contentTypesForPresign,
        userid: userId
      }

      const presignedResponse = await axios.post(API_URL, presignedPayload)

      // Parse the response
      let presignedData = presignedResponse.data
      if (typeof presignedData === 'string') {
        try {
          presignedData = JSON.parse(presignedData)
        } catch (parseError) {
          presignedData = presignedResponse.data
        }
      }

      if (presignedData.success === true && presignedData.files && presignedData.files.length > 0) {
        if (presignedData.files.length !== files.length) {
          throw new Error('Upload URL count mismatch')
        }

        setUploadProgress('Uploading files...')

        // Step 2: Upload each file to S3 via fetch (not axios — currentUserId ~44B bug)
        for (let index = 0; index < presignedData.files.length; index += 1) {
          const fileData = presignedData.files[index]
          const file = files[index]
          const fileItem = newFiles[index]
          const contentType =
            fileData.contentType ||
            contentTypesForPresign[index] ||
            'application/octet-stream'

          setSelectedFiles(prev => prev.map((f) => {
            if (f.originalName === fileItem.originalName && f.status === 'pending') {
              return { ...f, status: 'uploading', uploadUrl: fileData.uploadUrl, progress: 10 }
            }
            return f
          }))

          try {
            await putFileToPresignedUrl(fileData.uploadUrl, file, contentType)

            setSelectedFiles(prev => prev.map((f) => {
              if (f.originalName === fileItem.originalName) {
                return {
                  ...f,
                  status: 'uploaded',
                  s3Key: fileData.s3Key,
                  originalFileName: fileData.originalFileName || file.name,
                  progress: 100
                }
              }
              return f
            }))

            setS3Keys(prev => [...prev, fileData.s3Key])
            setOriginalFileNames(prev => [...prev, fileData.originalFileName || file.name])
          } catch (uploadError) {
            setSelectedFiles(prev => prev.map((f) => {
              if (f.originalName === fileItem.originalName) {
                return { ...f, status: 'error' }
              }
              return f
            }))
            throw uploadError
          }
        }

        setUploadProgress('All files uploaded successfully')
        toast.success(`${files.length} file(s) uploaded successfully`, { autoClose: 2000 })

        // Clear progress message after a short delay
        setTimeout(() => {
          setUploadProgress('')
        }, 2000)
      } else {
        throw new Error(presignedData.message || "Failed to get upload URLs")
      }
    } catch (error) {
      console.error("File upload error:", error)

      // Mark all new files as error
      setSelectedFiles(prev => prev.map((f) => {
        if (newFiles.some(nf => nf.originalName === f.originalName) && f.status !== 'uploaded') {
          return { ...f, status: 'error' }
        }
        return f
      }))

      if (error.response && error.response.data) {
        let errorData = error.response.data
        if (typeof errorData === 'string') {
          try {
            errorData = JSON.parse(errorData)
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(errorData.message || "Failed to upload files", { autoClose: 2000 })
      } else {
        toast.error("Network error. Please try again.", { autoClose: 2000 })
      }
    } finally {
      setIsUploading(false)
      e.target.value = '' // Reset file input
    }
  }

  // Handle new file removal
  const handleRemoveNewFile = (index) => {
    setSelectedFiles(prev => {
      const fileToRemove = prev[index]
      if (fileToRemove && fileToRemove.s3Key) {
        // Find the index of this s3Key in the arrays
        setS3Keys(prevKeys => {
          const keyIndex = prevKeys.findIndex(key => key === fileToRemove.s3Key)
          if (keyIndex !== -1) {
            // Remove from both arrays at the same index
            setOriginalFileNames(prevNames => {
              return prevNames.filter((name, idx) => idx !== keyIndex)
            })
            return prevKeys.filter((key, idx) => idx !== keyIndex)
          }
          return prevKeys
        })
      }
      // Mark file as removed
      return prev.map((f, i) => {
        if (i === index) {
          return { ...f, status: 'removed' }
        }
        return f
      })
    })
  }

  // Handle existing attachment removal
  const handleRemoveExistingAttachment = (attachmentIndex) => {
    const attachment = existingAttachments[attachmentIndex]
    if (attachment) {
      // Add to removal list (by index)
      setAttachmentsToRemove(prev => {
        // Check if already in list
        if (prev.some(item => item.index === attachmentIndex || item.url === attachment.url)) {
          return prev
        }
        return [...prev, { index: attachmentIndex, url: attachment.url }]
      })

      // Remove from display (but keep in state for API call)
      setExistingAttachments(prev => prev.filter((_, idx) => idx !== attachmentIndex))
    }
  }

  // Form validation
  const validateForm = () => {
    if (!formData.clientname.trim()) {
      toast.error("Client name is required", { autoClose: 2000 })
      return false
    }
    if (!contacts.length) {
      toast.error("At least one contact is required", { autoClose: 2000 })
      return false
    }

    for (let i = 0; i < contacts.length; i += 1) {
      const contact = contacts[i]
      const label = contacts.length > 1 ? ` (contact ${i + 1})` : ''
      const name = String(contact.contactPerson || '').trim()
      const email = String(contact.email || '').trim()
      const phone = String(contact.phonenumber || '').trim()
      const country = getCountryByIso(contact.countryIso)
      const limits = parseMobileDigitLimits(country?.mobile_digits)
      const hasEmail = Boolean(email)
      const hasPhone = Boolean(phone)

      if (!name && !hasEmail && !hasPhone) {
        toast.error(`Contact ${i + 1} is empty. Add details or remove it.`, {
          autoClose: 2000,
        })
        return false
      }

      if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error(`Please enter a valid email address${label}`, {
          autoClose: 2000,
        })
        return false
      }

      if (hasPhone && (phone.length < limits.min || phone.length > limits.max)) {
        toast.error(
          `Phone number must be ${formatPhoneDigitHint(limits)} for ${country?.country || 'selected country'}${label}`,
          { autoClose: 2000 }
        )
        return false
      }
    }

    const hasCompleteContact = contacts.some((c) => {
      const email = String(c.email || '').trim()
      const phone = String(c.phonenumber || '').trim()
      return Boolean(email) && Boolean(phone)
    })

    if (!hasCompleteContact) {
      toast.error(
        "At least one contact must have both email and phone number",
        { autoClose: 2500 }
      )
      return false
    }

    if (!formData.city.trim()) {
      toast.error("City is required", { autoClose: 2000 })
      return false
    }
    if (!formData.state.trim()) {
      toast.error("State is required", { autoClose: 2000 })
      return false
    }
    const gstNumber = formData.gstNumber.trim().toUpperCase()
    if (gstNumber) {
      if (!gstDetails || (gstDetails.gstin || '').toUpperCase() !== gstNumber) {
        toast.error('Please verify GST number before saving', { autoClose: 2000 })
        return false
      }
    }
    return true
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Clear any previous error messages
    setErrorMessage('')

    if (!validateForm()) {
      return
    }

    // Get currentUserId from localStorage
    const currentUserId = localStorage.getItem('id')
    // if (!currentUserId) {
    //   setErrorMessage("User session expired. Please login again.")
    //   setTimeout(() => {
    //     navigate('/')
    //   }, 2000)
    //   return
    // }

    setIsLoading(true)

    try {
      const primary =
        contacts.find(
          (c) =>
            String(c.email || '').trim() && String(c.phonenumber || '').trim()
        ) || contacts[0]
      const primaryCountry = getCountryByIso(primary.countryIso)
      const contactsPayload = contacts.map((c) => {
        const country = getCountryByIso(c.countryIso)
        const phoneDigits = String(c.phonenumber || '').trim()
        return {
          contactPerson: String(c.contactPerson || '').trim(),
          email: String(c.email || '').trim(),
          phonenumber: phoneDigits
            ? buildFullPhoneNumber(country?.calling_code, phoneDigits)
            : ''
        }
      })

      const payload = {
        action: "update-supplier",
        id: id,
        currentUserId: currentUserId,
        clientname: formData.clientname.trim(),
        email: String(primary.email || '').trim(),
        phonenumber: buildFullPhoneNumber(
          primaryCountry?.calling_code,
          String(primary.phonenumber || '').trim()
        ),
        contactPerson: String(primary.contactPerson || '').trim(),
        contacts: contactsPayload,
        city: formData.city.trim(),
        state: formData.state.trim(),
        address: formData.address.trim(),
        description: buildDescriptionPayload(descriptionEntries, newDescription),
        status: formData.status,
        accountNumber: formData.accountNumber ? formData.accountNumber.trim() : ''
      }

      // Add bank details if provided
      if (formData.ifscCode) {
        payload.ifscCode = formData.ifscCode.trim();
        payload.bankName = formData.bankName.trim();
        payload.branchName = formData.branchName.trim();
        payload.bankAddress = formData.bankAddress.trim();
        payload.bankCity = formData.bankCity.trim();
        payload.bankDistrict = formData.bankDistrict.trim();
        payload.bankState = formData.bankState.trim();
      }

      const gstNumber = formData.gstNumber.trim().toUpperCase()
      if (gstNumber) {
        payload.gstNumber = gstNumber
        if (gstDetails) {
          payload.gstStatus = gstDetails.status || ''
          payload.gstLegalName = gstDetails.legalName || ''
          payload.gstTradeName = gstDetails.tradeName || ''
          payload.gstConstitutionType = gstDetails.constitutionType || ''
          payload.taxPayerType = gstDetails.taxpayerType || ''
          payload.gstRegistrationDate = gstDetails.registrationDate || ''
          payload.gstState = gstDetails.state || ''
        }
      } else {
        payload.gstNumber = ''
      }

      // Handle attachments
      const validS3Keys = s3Keys.filter(key => key !== null && key !== undefined)
      const validOriginalFileNames = originalFileNames.filter(name => name !== null && name !== undefined)

      // Option 1: Add new files (default)
      if (validS3Keys.length > 0) {
        payload.s3Keys = validS3Keys
        if (validOriginalFileNames.length > 0) {
          payload.originalFileNames = validOriginalFileNames
        }
      }

      // Option 3: Remove specific attachments
      if (attachmentsToRemove.length > 0) {
        // Use the first removal method - by index
        const firstRemoval = attachmentsToRemove[0]
        if (firstRemoval.index !== undefined) {
          payload.removeAttachmentIndex = firstRemoval.index
        } else if (firstRemoval.url) {
          payload.removeAttachmentUrl = firstRemoval.url
        }
        // Note: If multiple removals needed, they should be handled in separate API calls
        // or the backend should support an array of indices/URLs
      }

      const response = await axios.post(API_URL, payload)

      // Parse the response - handle both string and object responses
      let responseData = response.data
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (parseError) {
          responseData = response.data
        }
      }

      // Check if update was successful
      if (responseData.success === true) {
        toast.success(responseData.message || "Supplier updated successfully", { autoClose: 2000 })
        setTimeout(() => {
          navigate('/supplyMaster')
        }, 2000)
      } else {
        setErrorMessage(responseData.message || "Failed to update supplier")
      }
    } catch (error) {
      console.error("Update supplier error:", error)
      if (error.response && error.response.data) {
        let errorData = error.response.data
        if (typeof errorData === 'string') {
          try {
            errorData = JSON.parse(errorData)
          } catch (e) {
            // Use error message as is
          }
        }
        setErrorMessage(errorData.message || "An error occurred while updating supplier")
      } else {
        setErrorMessage("Network error. Please check your connection and try again.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isFetching) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">Edit Supplier </h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/supplyMaster"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Edit Supplier</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/supplyMaster"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline card-tabs">
                  <div className="card-header p-0 pt-1 border-bottom-0">
                    <ul className="nav nav-tabs" role="tablist">
                      <li className="nav-item">
                        <button
                          type="button"
                          className={tabClass('profile')}
                          onClick={() => handleTabChange('profile')}
                        >
                          Profile
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={tabClass('description')}
                          onClick={() => handleTabChange('description')}
                        >
                          Description
                          {descriptionEntries.length > 0 ? ` (${descriptionEntries.length})` : ''}
                        </button>
                      </li>
                    </ul>
                  </div>
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      {activeTab === 'profile' && (
                        <>
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">Supplier Name</label>
                            <div className="col-12 col-md-8">
                              <input
                                type="text"
                                name="clientname"
                                className="form-control"
                                placeholder="Client Name"
                                value={formData.clientname}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">
                              Contacts
                            </label>
                            <div className="col-12 col-md-8">
                              {contacts.map((contact, index) => {
                                const country = getCountryByIso(contact.countryIso)
                                const limits = parseMobileDigitLimits(
                                  country?.mobile_digits
                                )
                                return (
                                  <div
                                    key={contact.id}
                                    className="border rounded p-3 mb-2"
                                    style={{ background: '#fafbfc' }}
                                  >
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <strong style={{ fontSize: '13px' }}>
                                        Contact {index + 1}
                                      </strong>
                                      <div className="d-flex" style={{ gap: '6px' }}>
                                        {index === contacts.length - 1 && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={addContact}
                                            title="Add contact"
                                          >
                                            <i className="fa-solid fa-plus" />
                                          </button>
                                        )}
                                        {contacts.length > 1 && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => removeContact(contact.id)}
                                            title="Remove contact"
                                          >
                                            <i className="fa-solid fa-trash" />
                                          </button>
                                        )}
                                      </div>
                                    </div>

                                    <div className="form-group mb-2">
                                      <label className="mb-1" style={{ fontSize: '12px' }}>
                                        Contact Person
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Contact Person"
                                        value={contact.contactPerson}
                                        onChange={(e) =>
                                          handleContactChange(
                                            contact.id,
                                            'contactPerson',
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>

                                    <div className="form-group mb-2">
                                      <label className="mb-1" style={{ fontSize: '12px' }}>
                                        Email
                                      </label>
                                      <input
                                        type="email"
                                        className="form-control"
                                        placeholder="Email"
                                        value={contact.email}
                                        onChange={(e) =>
                                          handleContactChange(
                                            contact.id,
                                            'email',
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>

                                    <div className="form-group mb-0">
                                      <label className="mb-1" style={{ fontSize: '12px' }}>
                                        Phone
                                      </label>
                                      <div className="input-group">
                                        <CountryCodeSelect
                                          countries={sortedCountries}
                                          value={contact.countryIso}
                                          onChange={(iso2) =>
                                            handleContactCountryChange(contact.id, iso2)
                                          }
                                          style={{ maxWidth: '220px', flex: '0 0 220px' }}
                                        />
                                        <input
                                          type="tel"
                                          className="form-control"
                                          placeholder={`Enter ${formatPhoneDigitHint(limits)}`}
                                          value={contact.phonenumber}
                                          onChange={(e) =>
                                            handleContactPhoneChange(
                                              contact.id,
                                              e.target.value,
                                              contact.countryIso
                                            )
                                          }
                                          onPaste={(e) =>
                                            handleContactPhonePaste(
                                              contact.id,
                                              e,
                                              contact.countryIso
                                            )
                                          }
                                          inputMode="numeric"
                                          maxLength={limits.max}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                              <small className="text-muted">
                                At least one contact needs both email and phone.
                                Extra contacts can be name-only or partial. Use + to
                                add more.
                              </small>
                            </div>
                          </div>

                          <div className="form-group row">
                             <label className="col-12 col-md-4 col-form-label">Address</label>
                            <div className="col-12 col-md-8">
                              <textarea
                               name="address"
                               className="form-control"
                               placeholder="Address"
                               rows={3}
                               value={formData.address}
                               onChange={handleChange}
                             />
                           </div>
                         </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">City</label>
                            <div className="col-12 col-md-8">
                              <input
                                type="text"
                                name="city"
                                className="form-control"
                                placeholder="City"
                                value={formData.city}
                                onChange={handleCityChange}
                                onKeyPress={(e) => {
                                  if (/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                                    e.preventDefault()
                                  }
                                }}
                                required
                              />
                            </div>
                          </div>
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">State</label>
                            <div className="col-12 col-md-8">
                              <input
                                type="text"
                                name="state"
                                className="form-control"
                                placeholder="State"
                                value={formData.state}
                                onChange={handleStateChange}
                                onKeyPress={(e) => {
                                  if (/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                                    e.preventDefault()
                                  }
                                }}
                                required
                              />
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">GST Number</label>
                            <div className="col-12 col-md-8">
                              <div className="input-group">
                                <input
                                  type="text"
                                  name="gstNumber"
                                  className="form-control"
                                  placeholder="GST Number (e.g., 29AAKCS7108C1ZV)"
                                  value={formData.gstNumber}
                                  onChange={handleGstChange}
                                  maxLength="15"
                                />
                                <div className="input-group-append">
                                  <button
                                    type="button"
                                    className="btn2 btn-info"
                                    onClick={handleFetchGstCaptcha}
                                    disabled={isFetchingGst || !formData.gstNumber}
                                  >
                                    {isFetchingGst ? (
                                      <>
                                        <i className="fa fa-spinner fa-spin mr-1"></i>
                                        Fetching...
                                      </>
                                    ) : (
                                      <>
                                        <i className="fa fa-download mr-1"></i>
                                        Fetch
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                              <small className="form-text text-muted">
                                Optional: Enter GST number, fetch captcha, then verify registration details
                              </small>

                              {showGstCaptcha && gstCaptchaImage && (
                                <div
                                  className="mt-3 p-3 border rounded"
                                  style={{ backgroundColor: '#f8f9fa' }}
                                >
                                  <p className="mb-2" style={{ fontSize: '13px', fontWeight: 600 }}>
                                    Enter the captcha below
                                  </p>
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="d-flex align-items-center flex-wrap">
                                      <img
                                        src={gstCaptchaImage}
                                        alt="GST Captcha"
                                        style={{
                                          height: '48px',
                                          border: '1px solid #ccc',
                                          borderRadius: '4px'
                                        }}
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-link btn-sm ml-2"
                                        onClick={async () => {
                                          try {
                                            setIsFetchingGst(true)
                                            await fetchGstCaptcha()
                                            toast.success('Captcha refreshed', { autoClose: 1500 })
                                          } catch {
                                            toast.error('Failed to refresh captcha', { autoClose: 2000 })
                                          } finally {
                                            setIsFetchingGst(false)
                                          }
                                        }}
                                        disabled={isFetchingGst}
                                      >
                                        Refresh captcha
                                      </button>
                                    </div>
                                    <button
                                      type="button"
                                      className="btn2 btn-info ml-2"
                                      onClick={handleVerifyGst}
                                      disabled={isVerifyingGst || !gstCaptchaInput.trim()}
                                    >
                                      {isVerifyingGst ? (
                                        <>
                                          <i className="fa fa-spinner fa-spin mr-1"></i>
                                          Verifying...
                                        </>
                                      ) : (
                                        <>
                                          <i className="fa fa-check-circle mr-1"></i>
                                          Verify
                                        </>
                                      )}
                                    </button>
                                  </div>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter captcha"
                                    value={gstCaptchaInput}
                                    onChange={(e) => setGstCaptchaInput(e.target.value)}
                                    maxLength={10}
                                    autoComplete="off"
                                  />
                                  <small className="form-text text-muted">
                                    Enter the captcha and click Verify
                                  </small>
                                </div>
                              )}

                              {gstDetails && (
                                <div
                                  className="mt-2 p-3"
                                  style={{
                                    backgroundColor: isGstActiveStatus(gstDetails.status)
                                      ? '#d4edda'
                                      : '#fff3cd',
                                    border: isGstActiveStatus(gstDetails.status)
                                      ? '1px solid #c3e6cb'
                                      : '1px solid #ffeeba',
                                    borderRadius: '4px'
                                  }}
                                >
                                  <div className="d-flex align-items-start">
                                    <i
                                      className={`fa ${isGstActiveStatus(gstDetails.status)
                                          ? 'fa-check-circle text-success'
                                          : 'fa-exclamation-triangle text-warning'
                                        } mr-2`}
                                      style={{ fontSize: '18px', marginTop: '2px' }}
                                    ></i>
                                    <div style={{ flex: 1 }}>
                                      <p
                                        className="mb-1"
                                        style={{
                                          fontSize: '13px',
                                          fontWeight: '600',
                                          color: isGstActiveStatus(gstDetails.status)
                                            ? '#155724'
                                            : '#856404'
                                        }}
                                      >
                                        {isGstActiveStatus(gstDetails.status)
                                          ? 'GST Verified Successfully'
                                          : 'GST Found But Not Active'}
                                      </p>
                                      <div
                                        style={{
                                          fontSize: '12px',
                                          color: isGstActiveStatus(gstDetails.status)
                                            ? '#155724'
                                            : '#856404'
                                        }}
                                      >
                                        <p className="mb-1">
                                          <strong>Status:</strong>{' '}
                                          <span
                                            className={`badge ${isGstActiveStatus(gstDetails.status)
                                                ? 'badge-success'
                                                : 'badge-warning'
                                              }`}
                                          >
                                            {gstDetails.status || 'Unknown'}
                                          </span>
                                        </p>
                                        {gstDetails.legalName && (
                                          <p className="mb-1">
                                            <strong>Legal Name:</strong> {gstDetails.legalName}
                                          </p>
                                        )}
                                        {gstDetails.tradeName && (
                                          <p className="mb-1">
                                            <strong>Trade Name:</strong> {gstDetails.tradeName}
                                          </p>
                                        )}
                                        {gstDetails.constitutionType && (
                                          <p className="mb-1">
                                            <strong>Constitution Type:</strong>{' '}
                                            {gstDetails.constitutionType}
                                          </p>
                                        )}
                                        {gstDetails.taxpayerType && (
                                          <p className="mb-1">
                                            <strong>Taxpayer Type:</strong> {gstDetails.taxpayerType}
                                          </p>
                                        )}
                                        {gstDetails.state && (
                                          <p className="mb-1">
                                            <strong>State:</strong> {gstDetails.state}
                                          </p>
                                        )}
                                        {gstDetails.registrationDate && (
                                          <p className="mb-0">
                                            <strong>Registration Date:</strong>{' '}
                                            {gstDetails.registrationDate}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {gstError && (
                                <div
                                  className="mt-2 p-2"
                                  style={{
                                    backgroundColor: '#f8d7da',
                                    border: '1px solid #f5c6cb',
                                    borderRadius: '4px'
                                  }}
                                >
                                  <div className="d-flex align-items-center">
                                    <i className="fa fa-exclamation-circle text-danger mr-2"></i>
                                    <span style={{ fontSize: '12px', color: '#721c24' }}>{gstError}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">Account Number</label>
                            <div className="col-12 col-md-8">
                              <input
                                type="text"
                                name="accountNumber"
                                className="form-control"
                                placeholder="Account Number"
                                value={formData.accountNumber}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, ''); // Only digits
                                  setFormData({ ...formData, accountNumber: value });
                                }}
                                maxLength="20"
                              />
                              <small className="form-text text-muted">
                                Optional: Enter bank account number
                              </small>
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">IFSC Code</label>
                            <div className="col-12 col-md-8">
                              <div className="input-group">
                                <input
                                  type="text"
                                  name="ifscCode"
                                  className="form-control"
                                  placeholder="IFSC Code (e.g., SBIN0001234)"
                                  value={formData.ifscCode}
                                  onChange={handleIfscChange}
                                  maxLength="11"
                                />
                                <div className="input-group-append">
                                  <button
                                    type="button"
                                    className="btn2 btn-info"
                                    onClick={handleVerifyIfsc}
                                    disabled={isVerifyingIfsc || !formData.ifscCode || formData.ifscCode.length !== 11}
                                  >
                                    {isVerifyingIfsc ? (
                                      <>
                                        <i className="fa fa-spinner fa-spin mr-1"></i>
                                        Verifying...
                                      </>
                                    ) : (
                                      <>
                                        <i className="fa fa-check-circle mr-1"></i>
                                        Verify
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                              <small className="form-text text-muted">
                                Optional: Enter IFSC code to auto-populate bank details
                              </small>

                              {/* IFSC Verification Results */}
                              {ifscDetails && (
                                <div className="mt-2 p-3" style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px' }}>
                                  <div className="d-flex align-items-start">
                                    <i className="fa fa-check-circle text-success mr-2" style={{ fontSize: '18px', marginTop: '2px' }}></i>
                                    <div style={{ flex: 1 }}>
                                      <p className="mb-1" style={{ fontSize: '13px', fontWeight: '600', color: '#155724' }}>
                                        IFSC Verified Successfully
                                      </p>
                                      <div style={{ fontSize: '12px', color: '#155724' }}>
                                        <p className="mb-1">
                                          <strong>Bank:</strong> {ifscDetails.BANK}
                                        </p>
                                        <p className="mb-1">
                                          <strong>Branch:</strong> {ifscDetails.BRANCH}
                                        </p>
                                        {ifscDetails.ADDRESS && (
                                          <p className="mb-1">
                                            <strong>Address:</strong> {ifscDetails.ADDRESS}
                                          </p>
                                        )}
                                        {ifscDetails.CITY && (
                                          <p className="mb-1">
                                            <strong>City:</strong> {ifscDetails.CITY}
                                          </p>
                                        )}
                                        {ifscDetails.DISTRICT && (
                                          <p className="mb-1">
                                            <strong>District:</strong> {ifscDetails.DISTRICT}
                                          </p>
                                        )}
                                        {ifscDetails.STATE && (
                                          <p className="mb-0">
                                            <strong>State:</strong> {ifscDetails.STATE}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* IFSC Error Message */}
                              {ifscError && (
                                <div className="mt-2 p-2" style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px' }}>
                                  <div className="d-flex align-items-center">
                                    <i className="fa fa-exclamation-circle text-danger mr-2"></i>
                                    <span style={{ fontSize: '12px', color: '#721c24' }}>{ifscError}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {userRole === 'admin' && (
                            <div className="form-group row">
                              <label className="col-12 col-md-4 col-form-label">Status</label>
                              <div className="col-12 col-md-8">
                                <select
                                  name="status"
                                  className="form-control"
                                  value={formData.status}
                                  onChange={handleChange}
                                  required
                                >
                                  <option value="active">Active</option>
                                  <option value="inactive">Inactive</option>
                                </select>
                              </div>
                            </div>
                          )}

                          {/* Existing Attachments Display */}
                          {existingAttachments.length > 0 && (
                            <div className="form-group row">
                              <label className="col-12 col-md-4 col-form-label">Existing Attachments</label>
                              <div className="col-12 col-md-8">
                                <div className="mt-2">
                                  {existingAttachments.map((attachment, index) => (
                                    <div key={index} className="mb-2 p-2 border rounded d-flex align-items-center justify-content-between">
                                      <div className="d-flex align-items-center flex-grow-1">
                                        <i className="fa-solid fa-file mr-2"></i>
                                        <span className="text-truncate" style={{ maxWidth: '300px' }}>
                                          {attachment.fileName}
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-link text-danger"
                                        onClick={() => handleRemoveExistingAttachment(index)}
                                        title="Remove attachment"
                                      >
                                        <i className="fa-solid fa-times"></i>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* New Attachments Upload */}
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">Attachments</label>
                            <div className="col-12 col-md-8">
                              <div>
                                <input
                                  type="file"
                                  id="attachment"
                                  className="form-control-file"
                                  onChange={handleFileChange}
                                  accept="image/*,.pdf,.doc,.docx"
                                  disabled={isUploading}
                                  multiple
                                />
                                <small className="form-text text-muted">
                                  Optional: Upload up to {MAX_FILES} total attachments (images or documents).
                                  Current: {existingAttachments.length} existing, {selectedFiles.filter(f => f.status !== 'removed').length} new.
                                </small>
                                {uploadProgress && (
                                  <div className="mt-2">
                                    <small className="text-primary">{uploadProgress}</small>
                                  </div>
                                )}
                              </div>

                              {/* Display new files being uploaded */}
                              {selectedFiles.filter(f => f.status !== 'removed').length > 0 && (
                                <div className="mt-3">
                                  {selectedFiles
                                    .map((fileItem, index) => {
                                      if (fileItem.status === 'removed') return null

                                      return (
                                        <div key={`${fileItem.originalName}-${index}`} className="mb-2 p-2 border rounded">
                                          <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center flex-grow-1">
                                              <i className="fa-solid fa-file mr-2"></i>
                                              <span className="mr-2 text-truncate" style={{ maxWidth: '200px' }}>
                                                {fileItem.originalName}
                                              </span>
                                              {fileItem.status === 'pending' && (
                                                <span className="badge badge-secondary">Pending</span>
                                              )}
                                              {fileItem.status === 'uploading' && (
                                                <span className="badge badge-info">
                                                  <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                                                  Uploading {fileItem.progress}%
                                                </span>
                                              )}
                                              {fileItem.status === 'uploaded' && (
                                                <span className="badge badge-success">
                                                  <i className="fa-solid fa-check-circle mr-1"></i>
                                                  Uploaded
                                                </span>
                                              )}
                                              {fileItem.status === 'error' && (
                                                <span className="badge badge-danger">
                                                  <i className="fa-solid fa-exclamation-circle mr-1"></i>
                                                  Error
                                                </span>
                                              )}
                                            </div>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-link text-danger"
                                              onClick={() => handleRemoveNewFile(index)}
                                              disabled={fileItem.status === 'uploading'}
                                              title="Remove file"
                                            >
                                              <i className="fa-solid fa-times"></i>
                                            </button>
                                          </div>
                                          {fileItem.status === 'uploading' && (
                                            <div className="mt-2">
                                              <div className="progress" style={{ height: '5px' }}>
                                                <div
                                                  className="progress-bar"
                                                  role="progressbar"
                                                  style={{ width: `${fileItem.progress}%` }}
                                                  aria-valuenow={fileItem.progress}
                                                  aria-valuemin="0"
                                                  aria-valuemax="100"
                                                ></div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )
                                    })
                                    .filter(item => item !== null)}
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}

                      {activeTab === 'description' && (
                        <div>
                          {descriptionEntries.length === 0 ? (
                           <div className="card shadow-sm">
                              <div className="card-body">
                                <div className="alert alert-info">
                                  No existing description entries. You can add
                                  one below.
                                </div>

                                <label className="font-weight-bold">
                                  Description
                                </label>
                                <textarea
                                  className="form-control"
                                  rows={3}
                                  placeholder="Enter supplier description..."
                                  value={newDescription}
                                  onChange={(e) =>
                                    setNewDescription(e.target.value)
                                  }
                                />
                              </div>
                            </div>
                              ) : (
                            <div className="supplier-description-timeline">
                              <div className="row align-items-center mb-3">
                                <div className="col-12 col-lg-8">
                                  <p className="text-muted small mb-2 mb-lg-0">
                                    {descriptionEntries.length}{' '}
                                    {descriptionEntries.length === 1 ? 'entry' : 'entries'} recorded.
                                  </p>
                                </div>
                                <div className="col-12 col-lg-4 text-lg-right">
                                  <span className="badge badge-secondary">
                                    {descriptionSearch.trim()
                                      ? `Showing ${filteredDescriptionDisplayEntries.length} of ${descriptionEntries.length}`
                                      : `${descriptionEntries.length} total`}
                                  </span>
                                </div>
                              </div>

                              <div className="input-group mb-3">
                                <div className="input-group-prepend">
                                  <span className="input-group-text">
                                    <i className="fa fa-search" />
                                  </span>
                                </div>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search description entries..."
                                  value={descriptionSearch}
                                  onChange={(e) => setDescriptionSearch(e.target.value)}
                                  autoComplete="off"
                                />
                                {descriptionSearch && (
                                  <div className="input-group-append">
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary"
                                      onClick={() => setDescriptionSearch('')}
                                      title="Clear search"
                                    >
                                      <i className="fa fa-times" />
                                    </button>
                                  </div>
                                )}
                              </div>

                              {filteredDescriptionDisplayEntries.length === 0 ? (
                                <div className="text-center text-muted py-4 border rounded bg-light">
                                  <i className="fa fa-search fa-2x mb-3 d-block" />
                                  <p className="mb-1">No entries match your search.</p>
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0"
                                    onClick={() => setDescriptionSearch('')}
                                  >
                                    Clear search
                                  </button>
                                </div>
                              ) : (
                                <div
                                  className="supplier-description-scroll"
                                  style={{
                                    maxHeight: 'min(60vh, 520px)',
                                    overflowY: 'auto',
                                    paddingRight: '6px'
                                  }}
                                >
                                  {filteredDescriptionDisplayEntries.map((entry) => (
                                    <div
                                      key={`entry-${entry.sourceIndex}`}
                                      className="card mb-3 shadow-sm"
                                      style={{
                                        borderLeft: entry.isLatest ? '4px solid #1199B2' : '4px solid #dee2e6'
                                      }}
                                    >
                                      <div className="card-body py-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                          <span className="badge badge-light text-dark">
                                            Entry {entry.entryNumber}
                                          </span>
                                          {entry.isLatest && (
                                            <span className="badge badge-info">Latest</span>
                                          )}
                                        </div>
                                        <textarea
                                          className="form-control"
                                          rows={3}
                                          value={entry.text}
                                          onChange={(e) =>
                                            handleDescriptionEntryChange(
                                              entry.sourceIndex,
                                              e.target.value
                                            )
                                          }
                                          style={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            lineHeight: 1.6
                                          }}
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {errorMessage && (
                      <div className="alert alert-danger mb-3" role="alert">
                        {errorMessage}
                      </div>
                    )}
                    {/* /.card-body */}
                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={isLoading || isUploading}
                      >
                        {isLoading ? "Updating..." : isUploading ? "Uploading..." : "Update"}
                      </button>
                      {/* <Link to="/supplyMaster" className="btn btn-secondary ml-2">Cancel</Link> */}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SupplyMasterEdit